import { lighten } from '@material-ui/core/styles'

export const SEASONS = {
  SPRING: 'spring',
  SUMMER: 'summer',
  AUTUMN: 'autumn',
  WINTER: 'winter',
}

/**
 * Northern hemisphere seasons by local calendar month. Change the boundaries
 * here (month is 0-based, like Date#getMonth).
 */
export const getSeason = (date = new Date()) => {
  const month = date.getMonth()
  if (month >= 2 && month <= 4) return SEASONS.SPRING
  if (month >= 5 && month <= 7) return SEASONS.SUMMER
  if (month >= 8 && month <= 10) return SEASONS.AUTUMN
  return SEASONS.WINTER
}

// Warm and pink tones the PGA Coach theme has no token for. Everything else
// below is read from the theme palette (navy primary, gold secondary).
const MAPLE_WARM_TONES = ['#C46A1D', '#D2802A', '#A9501A']
const MAPLE_STROKE = '#7A3A0E'
const BLOSSOM_PINKS = ['#F29BB9', '#F7BCD0', '#FFE9F0', '#E97B9E']
const BLOSSOM_GREEN = '#B9DFA8'
const BLOSSOM_STROKE = '#E58AA8'

/**
 * Particle look per season. `getColors(palette)` takes the MUI theme palette
 * and returns `fills` (cycled across particles) plus an optional `stroke`
 * outline. `sway` (default 1) scales the side sway and rotation. Summer is
 * intentionally absent: it plays no animation. Add a `shape` + `getColors` entry (and a shape in
 * `particle-shapes.js`) to enable it.
 */
export const SEASON_PARTICLES = {
  // Logo gold mixed in with warm maple tones; the brown outline keeps the
  // gold leaves reading as autumn leaves.
  [SEASONS.AUTUMN]: {
    shape: 'leaf',
    getColors: ({ secondary }) => ({
      fills: [
        MAPLE_WARM_TONES[0],
        secondary.main,
        MAPLE_WARM_TONES[1],
        MAPLE_WARM_TONES[2],
        secondary.main,
      ],
      stroke: MAPLE_STROKE,
    }),
  },
  // Light tints of the brand navy.
  [SEASONS.WINTER]: {
    shape: 'snowflake',
    getColors: ({ primary }) => ({
      fills: [
        lighten(primary.main, 0.25),
        lighten(primary.main, 0.4),
        lighten(primary.main, 0.55),
      ],
    }),
  },
  // Cherry blossom petals: blush pinks, a near-white pink and one fresh green
  // tint, outlined in rose so the light petals stay visible on white. They
  // sway and rotate wider than the other seasons.
  [SEASONS.SPRING]: {
    shape: 'petal',
    sway: 1.7,
    getColors: () => ({
      fills: [...BLOSSOM_PINKS, BLOSSOM_GREEN],
      stroke: BLOSSOM_STROKE,
    }),
  },
}

/**
 * The Coach logo is about 4.7:1, so sizes taken as a share of its width would
 * be taller than the logo. Scale M360's (square-logo) size fractions down so
 * particles stay a small fraction of the logo height.
 */
const SIZE_SCALE = 0.3

const particle = (x, size, delay, duration, direction) => ({
  x,
  size: size * SIZE_SCALE,
  delay,
  duration,
  direction,
})

/**
 * Fixed particle layout shared by every season (no randomness, so renders and
 * tests are stable). `x` is the left edge and `size` the width, both as a share
 * of the logo wrapper width; times are in seconds. `direction` mirrors the sway
 * so particles do not move in lockstep. The first five match M360's leaf fall.
 */
export const PARTICLES = [
  particle(0.04, 0.4, 0.2, 4.2, 1),
  particle(0.55, 0.32, 0.9, 3.8, -1),
  particle(0.28, 0.36, 1.7, 4.4, 1),
  particle(0.62, 0.28, 2.5, 3.9, -1),
  particle(0.12, 0.26, 3.2, 4.1, -1),
  particle(0.78, 0.3, 1.3, 4.0, 1),
  particle(0.42, 0.3, 2.1, 4.3, -1),
  particle(0.9, 0.27, 2.9, 4.1, 1),
]
